import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import siteLogo from "../../assets/logo-iniciales-oscuro.png";
import { resolveCourse } from "../../../educativo/components/courseRouteUtils";
import { serviceDetails } from "../../../servicios/assets/serviciosDetalle";
import {
  findSegmentBySlug,
  normalizeUtf8Text,
} from "../../../fisp-tv/data/noticias";
import usePageMeta from "./usePageMeta";

const DEFAULT_DESCRIPTION =
  "La comunidad de salud más grande de Latinoamérica. FISP combina educación, tecnología e innovación para formar profesionales comprometidos y difundir contenidos de calidad.";
const HIDDEN_ROBOTS = "noindex,nofollow,noarchive";
const VISIBLE_ROBOTS = "index,follow,max-image-preview:large";

const COURSE_FAMILY_LABELS = {
  cursos: "Curso",
  diplomaturas: "Diplomatura",
  posgrados: "Posgrado",
};

function cleanText(value, fallback = "") {
  return normalizeUtf8Text(String(value || fallback)).trim();
}

function compactText(value, fallback = DEFAULT_DESCRIPTION, maxLength = 180) {
  const normalized = cleanText(value, fallback).replace(/\s+/g, " ");

  if (!normalized) {
    return fallback;
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

function buildDefaultMeta(pathname) {
  return {
    title: "FISP | Educación, servicios y contenidos de salud",
    description: DEFAULT_DESCRIPTION,
    image: siteLogo,
    path: pathname,
    type: "website",
    robots: VISIBLE_ROBOTS,
  };
}

function buildRouteMeta(pathname) {
  const path = pathname || "/";
  const normalizedPath =
    path === "/"
      ? "/"
      : path.replace(/\/+$/, "").replace(/\.html$/i, "") || "/";
  const segments = normalizedPath.split("/").filter(Boolean);

  if (normalizedPath === "/index") {
    return buildDefaultMeta("/");
  }

  if (
    normalizedPath === "/educativo/admin" ||
    normalizedPath === "/fisp-tv/login" ||
    normalizedPath.startsWith("/fisp-tv/admin")
  ) {
    return {
      ...buildDefaultMeta(normalizedPath),
      robots: HIDDEN_ROBOTS,
      title: "FISP",
    };
  }

  const courseFamily = segments[0];
  const courseSlug = segments[1];
  const course = COURSE_FAMILY_LABELS[courseFamily]
    ? resolveCourse(courseFamily, courseSlug)
    : null;

  if (course) {
    const courseName = cleanText(course.nombre, "Programa FISP");
    const courseDescription = compactText(
      course.descripcionPreview || course.descripcion,
      `Conocé ${courseName} y descubrí su propuesta académica en FISP.`,
    );
    const familyLabel = COURSE_FAMILY_LABELS[courseFamily];

    if (segments[2] === "inscripcion" && segments[3] === "confirmacion") {
      return {
        title: `Confirmación de inscripción | ${courseName} | FISP`,
        description: `Revisá la confirmación de tu inscripción a ${courseName}.`,
        image: course.foto || siteLogo,
        path: normalizedPath,
        type: "website",
        robots: HIDDEN_ROBOTS,
      };
    }

    if (segments[2] === "inscripcion") {
      return {
        title: `Inscripción | ${courseName} | FISP`,
        description: `Completá tu inscripción a ${courseName} en FISP.`,
        image: course.foto || siteLogo,
        path: normalizedPath,
        type: "website",
        robots: VISIBLE_ROBOTS,
      };
    }

    return {
      title: `${courseName} | ${familyLabel} | FISP`,
      description: courseDescription,
      image: course.foto || siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (normalizedPath === "/") {
    return buildDefaultMeta(normalizedPath);
  }

  if (normalizedPath === "/contacto") {
    return {
      title: "Contacto | FISP",
      description:
        "Contactate con FISP para consultas sobre formación, alianzas, servicios y propuestas institucionales.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (normalizedPath === "/terminos-y-condiciones") {
    return {
      title: "Términos y condiciones | FISP",
      description:
        "Consultá los términos y condiciones del sitio web, inscripciones y contenidos de FISP.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (normalizedPath === "/fundacion" || normalizedPath === "/la-fundacion") {
    return {
      title: "Fundación | FISP",
      description:
        "Conocé la Fundación Iberoamericana de Salud Pública, su misión, su comunidad y sus líneas de trabajo en salud, educación e innovación.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (normalizedPath === "/fisp-uba") {
    return {
      title: "FISP + UBA | FISP",
      description:
        "Descubrí la propuesta académica conjunta entre FISP y la Facultad de Medicina de la Universidad de Buenos Aires.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (normalizedPath === "/educativo") {
    return {
      title: "Educativo | FISP",
      description:
        "Cursos, diplomaturas y posgrados en salud con propuestas actualizadas, modalidad flexible y docentes de trayectoria.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (normalizedPath === "/servicios") {
    return {
      title: "Servicios | FISP",
      description:
        "Soluciones para empresas e instituciones de salud: wellness corporativo, tecnología, marketing, capacitación, producción de contenidos y políticas públicas.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (segments[0] === "servicios" && segments[1]) {
    const service = serviceDetails[segments[1]];

    if (service) {
      const serviceTitle = cleanText(service.title, "Servicios FISP");
      const serviceDescription = compactText(
        service.subtitle || service.sections?.[0]?.paragraphs?.[0],
        `Conocé ${serviceTitle}, una línea de servicios de FISP.`,
      );

      return {
        title: `${serviceTitle} | Servicios | FISP`,
        description: serviceDescription,
        image: service.image || siteLogo,
        path: normalizedPath,
        type: "website",
        robots: VISIBLE_ROBOTS,
      };
    }
  }

  if (normalizedPath === "/fisp-tv") {
    return {
      title: "FISP TV | Noticias, entrevistas y contenidos de salud",
      description:
        "FISP TV reúne entrevistas, informes, columnas y contenidos audiovisuales sobre salud, bienestar e innovación.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (normalizedPath === "/fisp-tv/segmentos") {
    return {
      title: "Segmentos | FISP TV",
      description:
        "Explorá los segmentos de FISP TV: entrevistas, editoriales, innovación, bienestar y más.",
      image: siteLogo,
      path: normalizedPath,
      type: "website",
      robots: VISIBLE_ROBOTS,
    };
  }

  if (segments[0] === "fisp-tv" && segments[1] === "segmentos" && segments[2]) {
    const segment = findSegmentBySlug(segments[2]);

    if (segment) {
      return {
        title: `${cleanText(segment.label)} | FISP TV`,
        description: compactText(
          segment.description,
          `Mirá los contenidos de ${cleanText(segment.label)} en FISP TV.`,
        ),
        image: siteLogo,
        path: normalizedPath,
        type: "website",
        robots: VISIBLE_ROBOTS,
      };
    }
  }

  if (segments[0] === "fisp-tv" && segments[1]) {
    return {
      title: "Nota | FISP TV",
      description:
        "Leé una nota de FISP TV sobre salud, bienestar, innovación y actualidad del ecosistema sanitario.",
      image: siteLogo,
      path: normalizedPath,
      type: "article",
      robots: VISIBLE_ROBOTS,
    };
  }

  return buildDefaultMeta(normalizedPath);
}

function buildStructuredData(pathname) {
  if (pathname !== "/" && pathname !== "/index.html") {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.fisp.org.ar/#organization",
    name: "Fundacion Iberoamericana de Salud Publica",
    alternateName: "FISP",
    url: "https://www.fisp.org.ar/",
    description:
      "La comunidad de salud mas grande de Latinoamerica. FISP combina educacion, tecnologia e innovacion para formar profesionales comprometidos y difundir contenidos de calidad.",
    sameAs: [
      "https://www.instagram.com/fispsalud/",
      "https://www.facebook.com/fispsalud",
      "https://www.linkedin.com/company/fisp-org/",
      "https://www.youtube.com/@fisptv",
      "https://open.spotify.com/show/5VlWWkUHnHON4PIodMQw79",
      "https://www.tiktok.com/@fispsalud",
    ],
  };
}

export default function PublicRouteMetaManager() {
  const location = useLocation();
  const meta = useMemo(
    () => buildRouteMeta(location.pathname),
    [location.pathname],
  );
  const structuredData = useMemo(
    () => buildStructuredData(location.pathname),
    [location.pathname],
  );

  usePageMeta(meta);

  if (!structuredData) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}
